"use client";

import { usePathname } from "next/navigation";

/**
 * BreadcrumbList for the website, emitted on every page below the homepage.
 *
 * A client component only so it can read the path: usePathname resolves during server
 * rendering too, so this ships inside the server-rendered HTML like any other JSON-LD and
 * a crawler does not have to run JavaScript to see it.
 *
 * Google uses this to show "growth-sync.com › Product › Events" in place of a bare URL,
 * and it is one of the few structured data types that changes what a result looks like.
 */
const SITE_URL = "https://growth-sync.com";

/* Labels for a segment. A segment with no entry is title-cased from the slug, so a new
   page still produces a sane trail on the day it ships rather than nothing. */
const LABELS: Record<string, string> = {
  features: "Product",
  discover: "Discovery and matching",
  events: "Events",
  communities: "Communities",
  opportunities: "Opportunities and services",
  reputation: "Reputation",
  for: "Who it's for",
  founders: "Founders",
  "business-owners": "Business owners",
  pricing: "Pricing",
  about: "About",
  faq: "FAQ",
  safety: "Safety and trust",
  contact: "Contact",
  support: "Support",
  terms: "Terms",
  privacy: "Privacy",
  cookies: "Cookies",
  "child-safety": "Child safety",
  "account-deletion": "Delete your account",
  "data-deletion": "Delete your data",
  "get-app": "Get the app",
  "how-it-works": "How it works",
  "networking-for-entrepreneurs": "Networking for entrepreneurs",
  "meet-business-partners": "Meet business partners",
  "business-networking-app": "Business networking app",
};

function label(segment: string): string {
  return (
    LABELS[segment] ??
    segment.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}

export function SiteBreadcrumbs() {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);

  // The homepage is a single item, which is not a trail. Google ignores it either way.
  if (segments.length === 0) return null;

  const items = [
    { name: "GrowthSync", url: SITE_URL },
    ...segments.map((segment, index) => ({
      name: label(segment),
      url: `${SITE_URL}/${segments.slice(0, index + 1).join("/")}`,
    })),
  ];

  const json = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      // The last item is the current page, and schema.org says to omit its URL.
      ...(index === items.length - 1 ? {} : { item: item.url }),
    })),
  };

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }}
    />
  );
}
